import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { autoDateFormat } from "./autoDateFormate";
import { receiptPrintStyles } from "./txnDocument";

const cacheKey = (id) => `team_member_detail:${id}`;

export function cacheTeamMember(id, payload) {
  try {
    sessionStorage.setItem(cacheKey(id), JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

export function readCachedTeamMember(id) {
  try {
    const raw = sessionStorage.getItem(cacheKey(id));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** Normalize team member row into printable / viewable document */
export function buildTeamMemberDocument(member, brand = {}) {
  if (!member) return null;
  const companyName = brand.companyName || "EzyPayInd";
  const legalName = brand.legalName || "";
  const logoUrl = brand.logoUrl || "";

  const fields = [
    { label: "Joining Date", value: autoDateFormat(member.createdAt, "full") || "—" },
    { label: "Agent Name", value: member.name || "—" },
    { label: "User ID", value: member.userName || "—" },
    { label: "Branch Code", value: member.parentUserName || "—" },
    { label: "Mobile", value: member.phone || "—" },
    { label: "Email", value: member.email || "—" },
    { label: "Rank / Role", value: member.role ? String(member.role).toUpperCase() : "—" },
    {
      label: "Active",
      value:
        typeof member.isActive === "boolean"
          ? member.isActive
            ? "ACTIVE"
            : "INACTIVE"
          : "—",
    },
    {
      label: "Account Status",
      value: member.accountStatus || "REGISTERED",
    },
    { label: "Shop Name", value: member.shopName || member.businessName || "—" },
    { label: "City", value: member.city || member.address?.city || "—" },
    { label: "State", value: member.state || member.address?.state || "—" },
  ].filter((f) => f.value !== undefined && f.value !== null && f.value !== "");

  return {
    type: "team",
    id: member._id || member.id,
    companyName,
    legalName,
    logoUrl,
    title: "Team Member Profile",
    subtitle: "AGENT DETAILS",
    txnId: member.userName || member._id || "member",
    status: member.accountStatus || (typeof member.isActive === "boolean"
      ? member.isActive
        ? "ACTIVE"
        : "INACTIVE"
      : "REGISTERED"),
    fields,
    raw: member,
  };
}

export function buildMemberReceiptHtml(doc) {
  if (!doc) return "";
  const rows = (doc.fields || [])
    .map(
      (f) => `
      <div class="row">
        <span class="label">${escapeHtml(f.label)}</span>
        <span class="value">${escapeHtml(String(f.value))}</span>
      </div>`
    )
    .join("");

  return `
  <div class="receipt-doc" id="receipt-root">
    <div class="header">
      ${
        doc.logoUrl
          ? `<img class="logo" src="${escapeAttr(doc.logoUrl)}" alt="${escapeAttr(
              doc.companyName
            )}" />`
          : `<div class="logo-text">${escapeHtml(doc.companyName)}</div>`
      }
      <h1>${escapeHtml(doc.companyName)}</h1>
      ${doc.legalName ? `<p class="legal">${escapeHtml(doc.legalName)}</p>` : ""}
      <h2>${escapeHtml(doc.title)}</h2>
      <p class="subtitle">${escapeHtml(doc.subtitle)}</p>
    </div>
    <div class="amount-box">
      <div class="amount-label">Agent</div>
      <div class="amount-value capitalize" style="font-size:18px">${escapeHtml(
        doc.raw?.name || "—"
      )}</div>
      <div class="status status-success">${escapeHtml(String(doc.status))}</div>
    </div>
    <div class="fields">${rows}</div>
    <p class="footer">This is a system generated profile sheet. No signature required.</p>
  </div>`;
}

export function printTeamMember(doc) {
  const html = buildMemberReceiptHtml(doc);
  const win = window.open("", "_blank", "width=480,height=720");
  if (!win) return false;
  win.document.write(`<!doctype html><html><head><title>${doc.txnId || "Member"}</title>
    <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&display=swap" rel="stylesheet">
    <style>${receiptPrintStyles()}</style></head><body>${html}
    <script>window.onload=function(){setTimeout(function(){window.print();},250);}</script>
    </body></html>`);
  win.document.close();
  return true;
}

export async function downloadTeamMemberPdf(doc) {
  const wrap = document.createElement("div");
  wrap.style.position = "fixed";
  wrap.style.left = "-10000px";
  wrap.style.top = "0";
  wrap.innerHTML = `<style>${receiptPrintStyles()}</style>${buildMemberReceiptHtml(doc)}`;
  document.body.appendChild(wrap);

  const canvas = await html2canvas(wrap.querySelector("#receipt-root") || wrap, {
    scale: 2,
    useCORS: true,
    backgroundColor: "#ffffff",
    allowTaint: true,
  });

  if (wrap.parentNode) wrap.parentNode.removeChild(wrap);

  const imgData = canvas.toDataURL("image/png");
  const pdf = new jsPDF("p", "mm", "a4");
  const pageW = pdf.internal.pageSize.getWidth();
  const margin = 12;
  const usableW = pageW - margin * 2;
  const imgH = (canvas.height * usableW) / canvas.width;
  pdf.addImage(imgData, "PNG", margin, margin, usableW, imgH);
  pdf.save(`${doc.txnId || "member"}-profile.pdf`);
  return true;
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function escapeAttr(str) {
  return escapeHtml(str).replace(/'/g, "&#39;");
}
